/**
 * Create the FAQ Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createFAQAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'faq_agent',
    name: 'FAQ Agent',
    description: 'Handles FAQ and question answering from SAAED knowledge base',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt - checks for pending question from router redirect
    initialPrompt: 'You are now in FAQ mode. IMPORTANT: Check if there is a pendingQuestion in your context data. If there IS a pendingQuestion, immediately call search_faq with that question and answer it. If there is NO pendingQuestion, ask the user what question they have about SAAED services, fees, or procedures.',
    
    // Must have language and be coming from router
    entryGuardrails: requireLanguage(),
    
    // Can go back to router or switch to other services
    exitGuardrails: {
      transitions: new Map([
        ['back_to_menu', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['switch_renewal', {
          target: { agentId: 'renewal_input_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['switch_accident', {
          target: { agentId: 'router_agent' }, // Will change when accident flow is implemented
          guardrails: [languageDetectedGuardrail],
        }],
        ['switch_lost_report', {
          target: { agentId: 'lost_report_upload_extract_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        
        // Determine language for formatting
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[FAQAgent] Search query:', question);
        console.log('[FAQAgent] Results found:', searchResults.length);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: searchResults.length > 0 
              ? `Found ${searchResults.length} result(s)`
              : 'No matching results found',
          },
        };
      }
      
      if (toolName === 'back_to_menu') {
        console.log('[FAQAgent] User wants to go back to menu');
        
        return {
          toolName,
          args,
          result: { 
            success: true,
            message: 'Returning to main menu',
          },
          triggersTransition: true,
          transitionTo: 'router_agent',
        };
      }
      
      if (toolName === 'switch_to_service') {
        const service = args.service || '';
        console.log('[FAQAgent] User wants to switch to service:', service);
        
        // Map service name to agent ID
        let targetAgent: string;
        let message: string;
        
        switch (service) {
          case 'renewal':
            targetAgent = 'renewal_input_agent';
            message = 'Switching to report renewal service';
            break;
          case 'accident':
            targetAgent = 'accident_start_agent';
            message = 'Switching to accident reporting';
            break;
          case 'lost_report':
            targetAgent = 'lost_report_upload_extract_agent';
            message = 'Switching to lost report search';
            break;
          default:
            targetAgent = 'router_agent';
            message = 'Returning to main menu';
        }
        
        return {
          toolName,
          args,
          result: { 
            success: true,
            service,
            message,
          },
          triggersTransition: true,
          transitionTo: targetAgent,
        };
      }
      
      // Cross-flow navigation - Accident reporting (direct tool)
      if (toolName === 'start_accident_reporting') {
        console.log('[FAQAgent] Cross-flow: Starting accident reporting');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting accident reporting' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
        };
      }
      
      // Cross-flow navigation - Renewal (direct tool)
      if (toolName === 'start_renewal') {
        const reportNumber = args.report_number;
        console.log('[FAQAgent] Cross-flow: Starting renewal', reportNumber ? `for report ${reportNumber}` : '');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting renewal process' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
          transitionData: {
            prefilledReportNumber: reportNumber,
          },
        };
      }
      
      // Cross-flow navigation - Lost report search (direct tool)
      if (toolName === 'start_lost_report_search') {
        console.log('[FAQAgent] Cross-flow: Starting lost report search');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      console.log('[FAQAgent] Activated - ready to answer questions');
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[FAQAgent] Deactivated');
    },
  };
}

export default createFAQAgent;

